import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, Transaction } from '@/lib/api';
import type { BatchUpdateTransactionPatch } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency } from '@/lib/format';
import { getErrorMessage, isNetworkError, getNetworkErrorMessage } from '@/lib/errors';
import { QUERY_KEYS, QUERY_STALE_TIMES } from '@/lib/constants';
import { invalidateFinancialQueries } from '@/hooks/queryInvalidation';

export interface TransactionFilters {
  type?: 'income' | 'expense';
  from?: string;
  to?: string;
  category?: string;
  limit?: number;
  offset?: number;
}

interface UseTransactionsOptions {
  filters?: TransactionFilters;
  enabled?: boolean;
}

export function useTransactions(options: UseTransactionsOptions = {}) {
  const { filters, enabled = true } = options;

  return useQuery({
    queryKey: [QUERY_KEYS.transactions, filters],
    queryFn: () => api.getTransactions(filters),
    enabled,
    staleTime: QUERY_STALE_TIMES.transactions,
  });
}

interface CreateTransactionData {
  amount: number;
  category_id: string;
  date: string;
  description: string;
  type: 'income' | 'expense';
}

export function useCreateTransaction() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateTransactionData) => api.createTransaction(data),
    onSuccess: (newTransaction: Transaction) => {
      invalidateFinancialQueries(queryClient);
      const typeLabel = newTransaction.type === 'income' ? 'receita' : 'despesa';
      toast({
        title: 'Transação adicionada',
        description: `${formatCurrency(newTransaction.amount)} de ${typeLabel} registrada.`,
      });
    },
    onError: (error: unknown) => {
      const errorInfo = isNetworkError(error) ? getNetworkErrorMessage() : getErrorMessage(error);
      toast({
        title: errorInfo.title,
        description: errorInfo.description,
        variant: 'destructive',
      });
    },
  });
}

export function useBatchCreateTransactions() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (transactions: CreateTransactionData[]) => api.batchCreateTransactions(transactions),
    onSuccess: (created: Transaction[]) => {
      invalidateFinancialQueries(queryClient);
      toast({
        title: `${created.length} ${created.length === 1 ? 'transação registrada' : 'transações registradas'}`,
        description: 'Lançamentos salvos com sucesso.',
      });
    },
    onError: (error: unknown) => {
      const errorInfo = isNetworkError(error) ? getNetworkErrorMessage() : getErrorMessage(error);
      toast({
        title: errorInfo.title,
        description: errorInfo.description,
        variant: 'destructive',
      });
    },
  });
}

export function useBatchUpdateTransactions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transactions: BatchUpdateTransactionPatch[]) =>
      api.batchUpdateTransactions(transactions),
    onSuccess: () => {
      invalidateFinancialQueries(queryClient);
    },
  });
}

interface UpdateTransactionData {
  id: string;
  amount: number;
  category_id: string;
  date: string;
  description: string;
  type: 'income' | 'expense';
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdateTransactionData) => api.updateTransaction(data),
    onSuccess: () => {
      invalidateFinancialQueries(queryClient);
      toast({
        title: 'Transação atualizada',
        description: 'Suas alterações foram salvas.',
      });
    },
    onError: (error: unknown) => {
      const errorInfo = isNetworkError(error) ? getNetworkErrorMessage() : getErrorMessage(error);
      toast({
        title: errorInfo.title,
        description: errorInfo.description,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteTransaction() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.deleteTransaction(id),
    onSuccess: () => {
      invalidateFinancialQueries(queryClient);
      toast({
        title: 'Transação excluída',
        description: 'A transação foi removida.',
      });
    },
    onError: (error: unknown) => {
      const errorInfo = isNetworkError(error) ? getNetworkErrorMessage() : getErrorMessage(error);
      toast({
        title: errorInfo.title,
        description: errorInfo.description,
        variant: 'destructive',
      });
    },
  });
}
